import { useCallback, useEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AnalyticsProvider, SentryErrorBoundary } from "@opero/shared/analytics";
import { APP_URL } from "./lib/polaris-client";
import { ThemeProvider } from "./context/ThemeContext";
import { FeatureFlagProvider } from "./context/FeatureFlagContext";
import { Layout } from "./components/Layout";
import { HomePage } from "./pages/HomePage";
import { BlogPage } from "./pages/BlogPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { AboutPage } from "./pages/AboutPage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { CookiePolicyPage } from "./pages/CookiePolicyPage";
import { DpaPage } from "./pages/DpaPage";
import { SecurityPage } from "./pages/SecurityPage";
import { ContactPage } from "./pages/ContactPage";
import { PressPage } from "./pages/PressPage";
import { DocsPage } from "./pages/DocsPage";
import { CommunityPage } from "./pages/CommunityPage";
import { StatusPage } from "./pages/StatusPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { SharedDocumentPage } from "./pages/SharedDocumentPage";
import { FeatureGate } from "./components/FeatureGate";
import { LoginModal } from "./components/LoginModal";
import { SignupModal } from "./components/SignupModal";
import { VerifyEmailModal } from "./components/VerifyEmailModal";
import { AcceptInviteModal } from "./components/AcceptInviteModal";
import { SsoModal } from "./components/SsoModal";
import { WorkspaceSetupPage } from "./features/workspace-setup/pages/WorkspaceSetupPage";
import { AuthCallbackPage } from "./pages/AuthCallbackPage";

/**
 * Top-level paths that belong to opero-app, not the marketing site.
 *
 * Deep links to them are minted by the backend (notification `actionUrl`s),
 * pasted between teammates and bookmarked — and people routinely drop the port
 * or land on the marketing origin, where every one of those links used to 404.
 *
 * An explicit list rather than a catch-all: a catch-all would swallow every
 * genuine 404 on the marketing site and bounce typos into the app, where they
 * would 404 a second time behind a login wall. Keep in sync with the top-level
 * routes in `apps/opero-app/src/App.tsx`.
 */
const APP_PATH_PREFIXES = [
  "dashboard",
  "projects",
  "tasks",
  "workflows",
  "notes",
  "documents",
  "reports",
  "calendar",
  "team",
  "messages",
  "notifications",
  "atlas",
  "profile",
  "billing",
  "settings",
] as const;

/**
 * Forwards the current URL to the same path on opero-app, query and hash
 * intact — `?taskId=…`, `?tab=tasks` and the like are exactly what makes these
 * links worth forwarding rather than 404ing.
 *
 * `replace` keeps the marketing origin out of the history stack, so Back from
 * the app does not bounce straight through the redirect again.
 */
function AppRedirect() {
  const location = useLocation();
  useEffect(() => {
    const base = APP_URL.replace(/\/$/, "");
    window.location.replace(
      `${base}${location.pathname}${location.search}${location.hash}`,
    );
  }, [location.pathname, location.search, location.hash]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  // Normalize so backend-emitted links with a trailing slash still match.
  const path = location.pathname.replace(/\/+$/, "") || "/";

  // Modal state derived from URL — survives refresh
  const loginOpen = path === "/login" || path === "/forgot-password";
  const signupOpen = path === "/signup";
  const verifyOpen = path === "/verify-email";
  const resetOpen = path === "/reset-password";
  const acceptInviteMatch = path.match(/^\/accept-invite(?:\/([^/?#]+))?$/);
  const acceptInviteOpen = acceptInviteMatch !== null;
  const acceptInviteToken = acceptInviteMatch?.[1] ?? "";
  const ssoOpen = path === "/sso";

  const goHome = useCallback(
    () => navigate("/", { replace: true }),
    [navigate],
  );

  // Single modal with the right initial view based on path
  const loginOrResetOpen = loginOpen || resetOpen;
  const loginModalView = resetOpen
    ? ("reset-password" as const)
    : path === "/forgot-password"
      ? ("forgot-password" as const)
      : ("login" as const);

  return (
    <>
      <Routes>
        {/* Workspace setup — full page, no marketing chrome */}
        <Route path="setup" element={<WorkspaceSetupPage />} />
        <Route path="auth/callback" element={<AuthCallbackPage />} />
        {/* `<seg>/*` also matches the bare `/<seg>`, so one route per prefix
         * covers both the section root and everything beneath it. */}
        {APP_PATH_PREFIXES.map((prefix) => (
          <Route key={prefix} path={`${prefix}/*`} element={<AppRedirect />} />
        ))}
        <Route
          element={
            <Layout
              onLoginClick={() => navigate("/login")}
              onSignupClick={() => navigate("/signup")}
            />
          }
        >
          <Route index element={<HomePage />} />
          <Route path="login" element={<HomePage />} />
          <Route path="signup" element={<HomePage />} />
          <Route path="verify-email" element={<HomePage />} />
          <Route path="forgot-password" element={<HomePage />} />
          <Route path="reset-password" element={<HomePage />} />
          <Route path="accept-invite" element={<HomePage />} />
          <Route path="accept-invite/:token" element={<HomePage />} />
          <Route path="sso" element={<HomePage />} />
          <Route
            path="blog"
            element={
              <FeatureGate flag="BLOG">
                <BlogPage />
              </FeatureGate>
            }
          />
          <Route
            path="blog/:slug"
            element={
              <FeatureGate flag="BLOG">
                <BlogPostPage />
              </FeatureGate>
            }
          />
          <Route path="about" element={<AboutPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="cookies" element={<CookiePolicyPage />} />
          <Route path="dpa" element={<DpaPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="press" element={<PressPage />} />
          <Route path="docs" element={<DocsPage />} />
          {/* <Route path="api" element={<ApiReferencePage />} /> */}
          <Route path="community" element={<CommunityPage />} />
          <Route path="status" element={<StatusPage />} />
          {/* Recipient side of a document public link. Unauthenticated by
            * design, so it lives here rather than in opero-app. */}
          <Route path="shared/:token" element={<SharedDocumentPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>

      <LoginModal
        isOpen={loginOrResetOpen}
        onClose={goHome}
        onSwitchToSignup={() => navigate("/signup", { replace: true })}
        initialView={loginModalView}
      />
      <SignupModal
        isOpen={signupOpen}
        onClose={goHome}
        onSwitchToLogin={() => navigate("/login", { replace: true })}
      />
      <VerifyEmailModal isOpen={verifyOpen} onClose={goHome} />
      <AcceptInviteModal
        isOpen={acceptInviteOpen}
        token={acceptInviteToken}
        onClose={goHome}
        onSwitchToLogin={() => navigate("/login", { replace: true })}
      />
      <SsoModal
        isOpen={ssoOpen}
        onClose={goHome}
        onSwitchToLogin={() => navigate("/login", { replace: true })}
      />
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <FeatureFlagProvider>
        <AnalyticsProvider>
          <SentryErrorBoundary>
            <AppContent />
          </SentryErrorBoundary>
        </AnalyticsProvider>
      </FeatureFlagProvider>
    </ThemeProvider>
  );
}
